import React, { useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart3, Download, FileSpreadsheet, Users, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function Reports() {
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState({
    start_date: format(new Date(), 'yyyy-MM-01'),
    end_date: format(new Date(), 'yyyy-MM-dd')
  });

  const handleExportEmployees = async () => {
    setExporting(true);
    try {
      const response = await api.get('/export/employees', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `employees_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export des employés téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const handleExportAttendance = async () => {
    setExporting(true);
    try {
      const response = await api.get('/export/attendance', {
        params: dateRange,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_${dateRange.start_date}_${dateRange.end_date}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export des présences téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading font-bold text-3xl text-slate-900">Rapports & Statistiques</h1>
        <p className="text-slate-600 mt-1">Analyse des données RH et exports Excel</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-slate-200 hover:shadow-md transition-all">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Export Employés
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Exportez la liste complète des employés avec leurs informations : matricule, nom, email, poste, salaire, horaires et solde de congés.
            </p>
            <Button
              onClick={handleExportEmployees}
              disabled={exporting}
              className="w-full bg-primary hover:bg-primary-700"
              data-testid="export-employees-button"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {exporting ? 'Export en cours...' : 'Exporter en Excel'}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-slate-200 hover:shadow-md transition-all">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-success" />
              Export Présences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Exportez les données de présence pour une période donnée avec les heures travaillées, retards et heures supplémentaires.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Date début</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={dateRange.start_date}
                  onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                  data-testid="start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">Date fin</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={dateRange.end_date}
                  onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                  data-testid="end-date-input"
                />
              </div>
            </div>
            <Button
              onClick={handleExportAttendance}
              disabled={exporting}
              className="w-full bg-success hover:bg-success/90"
              data-testid="export-attendance-button"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {exporting ? 'Export en cours...' : 'Exporter en Excel'}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Statistiques Disponibles
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-slate-200 bg-slate-50">
              <CardContent className="p-6 text-center">
                <Download className="w-8 h-8 text-primary mx-auto mb-2" />
                <h3 className="font-heading font-semibold">Format Excel</h3>
                <p className="text-sm text-slate-600 mt-1">Exports compatibles Excel et Google Sheets</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-slate-50">
              <CardContent className="p-6 text-center">
                <Calendar className="w-8 h-8 text-success mx-auto mb-2" />
                <h3 className="font-heading font-semibold">Périodes Personnalisées</h3>
                <p className="text-sm text-slate-600 mt-1">Sélectionnez n'importe quelle période</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-slate-50">
              <CardContent className="p-6 text-center">
                <BarChart3 className="w-8 h-8 text-warning mx-auto mb-2" />
                <h3 className="font-heading font-semibold">Données Complètes</h3>
                <p className="text-sm text-slate-600 mt-1">Toutes les métriques RH disponibles</p>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
